/**
 * Copyright (c) HashiCorp, Inc.
 * SPDX-License-Identifier: MPL-2.0
 */

import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  CatalogTableRow,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import CustomScaffolderPage from './components/CustomScaffolder/CustomScaffolderPage'
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { TechRadarPage } from '@backstage/plugin-tech-radar';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage, SettingsLayout } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import { AlertDisplay, Link, OAuthRequestDialog, SignInPage } from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { githubAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { terraformApiRef } from '@internal/plugin-terraform/src/api';
import { TerraformApi } from '@internal/plugin-terraform/src/api/types';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import StarBorder from '@mui/icons-material/StarBorder';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useStarredEntities } from '@backstage/plugin-catalog-react';
import Star from '@material-ui/icons/Star';
import withStyles from '@material-ui/core/styles/withStyles';
import Resourcecost from './components/resourcecost/resourcecost';
import CustomSettings from './components/pages/CustomSettings';
import S3Buckets from './components/buckets/S3Buckets';
import NetworkInterfaces from './components/networkInterfaces/NetworkInterfaces';
import UnusedVolumes from './components/unusedvolumes/UnusedVolumes';
import AWSComponent from './components/AWSComponent/AWSComponent';
import EC2Snapshots from './components/ec2snapshots/ec2snapshots';
import UserManagement from './components/usermanagement/UserManagement';
import GithubPage from './components/GithubComponent/GithubPage';
import Terraform from './components/TerraformComponent/TerraformPage';
import HomePage from './components/home/HomePage';
import CostOptimization from './components/CostOptimization/CostOptimization';
import GeneralSetting from './components/pages/GeneralSetting';
import RDSComponent from './components/rds/RDSComponent';
import RDSSnapshot from './components/rds-snapshot/RDSSnapshot';
import Heatmap from './components/HeatMapComponent/Heatmap';
import JenkinsJobs from './components/Jenkins/JenkinsPage';
import BuildLogsPage from './components/Jenkins/BuildLogsPage';
import EC2SavingsPlans from './components/EC2/ec2page';
import VirtualPrivateCloud from './components/VirtualPrivateCloud/VirtualPrivateCloud';
import KeyManagementService from './components/kmsKeys/KeyManagementService';
import CostOptimizationHub from './components/CostOptimizationHub/CostOptimizationHubPage';
//import { OpenCostPage } from '@backstage-community/plugin-opencost';
import { myOidcAuthApiRef } from './apis';
import TenantApiExplorerPage from './components/TenantApiExplorerPage/TenantApiExplorerPage';
import CustomSettingsPage from './components/CustomSettingsPage'
const app = createApp({
  components: {
    SignInPage: props => 
   (
      <SignInPage
        {...props}
        provider={{
          id: 'my-oidc-provider',
          title: 'Amazon Cognito',
          message: 'Sign in using Cognito',
          apiRef: myOidcAuthApiRef,
	  options: {
      		flow: 'redirect',
    	},
        }}
      />
    ),
  },
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});


async function handleDeleteWorkspace(entity: any, api: TerraformApi) {

  console.debug('handleDeleteWorkspace', entity)
  if (confirm(`Are sure want to delete ${entity?.metadata?.name} ?`)) {
    // const terraformApi = useApi(terraformApiRef)
    console.log(`entity is ${JSON.stringify(entity)}`)
    console.log(`api is ${JSON.stringify(api)}`)
    await api.deleteComponent(entity.metadata.name)
    await api.deleteWorkspace(entity.metadata.annotations["terraform.io/organization"], entity.metadata.annotations["terraform.io/workspace"])
    // Will be changing this confirm option and using similar to what backstage is using.
    //   // const response = await fetch(
    //   //   `${apiUrl}/organizations/${organization}/workspaces/${workspaceName}`,
    //   //   {
    //   //     method: 'GET',
    //   //   },
    //   // );

    //   // if (!response.ok) {
    //   //   throw new Error(
    //   //     `failed to fetch data, status ${response.status}: ${response.statusText}`,
    //   //   );
    //   // }

    //   // return await response.json();
    window.location.reload();
  }
}

const YellowStar = withStyles({
  root: {
    color: '#f3ba37',
  },
})(Star);

const defaultWorkspaceActions = [
  ({ entity }) => {
    const title = 'Open link';
    const link = entity?.metadata?.annotations?.["backstage.io/view-url"];
    return {
      icon: () => <OpenInNewIcon />,
      tooltip: title,
      onClick: () => window.open(link, "_blank"),
    };
  },
  ({ entity }) => {
    const title = 'Edit';
    const link = entity?.metadata?.annotations?.["backstage.io/edit-url"];
    return {
      icon: () => <EditIcon />,
      tooltip: title,
      onClick: () => window.open(link, "_blank"),
    };
  },
  ({ entity }) => {
    const { isStarredEntity, toggleStarredEntity } = useStarredEntities();
    const isStarred = isStarredEntity(entity);
    return {
      cellStyle: { paddingLeft: '1em' },
      icon: () => (isStarred ? <YellowStar /> : <StarBorder />),
      tooltip: isStarred ? 'Remove from favorites' : 'Add to favorites',
      onClick: () => toggleStarredEntity(entity),
    };
  },
  ({ entity }) => {
    const terraformApi = useApi(terraformApiRef)
    const title = 'Delete';
    return {
      icon: () => <DeleteIcon />,
      tooltip: title,
      onClick: () => handleDeleteWorkspace(entity, terraformApi),
    };
  },

];

const catalogColumns = [
  {
    field: "entity.metadata.name",
    title: "Name",
    highlight: true,
    render: ({ entity }) => {
      return <Link to={`/catalog/default/component/${entity?.metadata?.name}`}>{entity?.metadata?.name}</Link>
    }
  },
  {
    field: "entity.spec.description",
    title: "description"
  },
  {
    field: "entity.spec.owner",
    title: "owner",
    render: ({ entity }) => {
      const mapping = entity.spec.owner && entity.spec.owner.toString().replace(":", "/");
      return <Link to={`/catalog/default/${mapping}`}>{entity?.spec?.owner}</Link>
    }
  },
  {
    field: "entity.spec.type",
    title: "service"
  },
  {
    field: "entity.spec.lifecycle",
    title: "lifecycle"
  },

];


const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="home" />} />
    <Route path="/home" element={<HomePage
    />} />
    {/* <Route path="/" element={<Navigate to="catalog" />} /> */}
    <Route path="/catalog" element={<CatalogIndexPage
      columns={catalogColumns}
      actions={defaultWorkspaceActions}
    />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route path="/resourcecost" element={<Resourcecost />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/custom-create" element={<CustomScaffolderPage />} />
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<TenantApiExplorerPage />} />
    {/* <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    /> */}
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    {/* <Route path="/settings" element={<UserSettingsPage />} /> */}
    <Route path="/settings" element={<CustomSettingsPage />}>
      {/* <SettingsLayout.Route path="/custom-settings" title="Configurations">
        <CustomSettings />
      </SettingsLayout.Route> */}
    </Route>;
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    {/* <Route path='/custom-settings' element={<CustomSettings/>} /> */}
    <Route path='/s3-buckets' element={<S3Buckets />} />
    <Route path='/network-interfaces' element={<NetworkInterfaces />} />
    <Route path='/aws' element={<AWSComponent />} />
    <Route path='/unused-volumes' element={<UnusedVolumes />} />
    <Route path='/ec2snapshots' element={<EC2Snapshots />} />
    <Route path='/rds' element={<RDSComponent />} />
    <Route path='/rds-snapshot' element={<RDSSnapshot />} />
    <Route path="/user-management" element={<UserManagement />} />
    <Route path="/repositories" element={<GithubPage />} />
    <Route path='/terraform' element={<Terraform />} />
    <Route path='/cost-summary' element={<CostOptimization />} />
    <Route path='/heatmap' element={<Heatmap />} />
    <Route path='/jenkins-jobs' element={<JenkinsJobs />} />
    <Route path="/build-logs" element={<BuildLogsPage />} />
    <Route path="/ec2" element={<EC2SavingsPlans />} />
    <Route path="/vpc" element={<VirtualPrivateCloud />} />
    <Route path="/kms-cost" element={<KeyManagementService />} />
    <Route path="/cost-optimization-hub" element={<CostOptimizationHub />} />    
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
